/**
 * useSibaScoreConfig — full SibaScore admin configuration (identity, bounds,
 * ranges, signals, description). Powers the gauge + any future score
 * disclosure surface. Falls back to seeded defaults so the UI always renders.
 *
 * `useSibaScoreRanges` is preserved as a thin alias for backward compatibility
 * with the iteration_95 shape.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface SibaScoreIdentity {
  display_name: string;
  trademark: string;
  definition: string;
}

export interface SibaScoreBounds {
  min_score: number;
  max_score: number;
}

export interface SibaScoreRange {
  min: number;
  max: number;
  label: string;
  color: string;
}

export interface SibaScoreSignal {
  name: string;
  enabled: boolean;
  weight: number;
  description: string;
  order: number;
}

export interface SibaScoreConfig {
  identity: SibaScoreIdentity;
  bounds: SibaScoreBounds;
  description: string;
  ranges: SibaScoreRange[];
  signals: SibaScoreSignal[];
  updated_at?: string | null;
}

export const DEFAULT_SIBASCORE_IDENTITY: SibaScoreIdentity = {
  display_name: "SibaScore",
  trademark: "\u2122",
  definition: "Smart Intelligence for Buying Assessment",
};

export const DEFAULT_SIBASCORE_BOUNDS: SibaScoreBounds = {
  min_score: 0,
  max_score: 1000,
};

export const DEFAULT_SIBASCORE_RANGES: SibaScoreRange[] = [
  { min: 900, max: 1000, label: "Exceptional",   color: "#10B981" },
  { min: 800, max: 899,  label: "Excellent",     color: "#22C55E" },
  { min: 700, max: 799,  label: "Very Good",     color: "#FF7A00" },
  { min: 600, max: 699,  label: "Good",          color: "#F59E0B" },
  { min: 500, max: 599,  label: "Average",       color: "#F97316" },
  { min: 400, max: 499,  label: "Below Average", color: "#EF4444" },
  { min: 0,   max: 399,  label: "High Caution",  color: "#B91C1C" },
];

export const DEFAULT_SIBASCORE_SIGNALS: SibaScoreSignal[] = [
  { name: "Customer Sentiment",    enabled: true, weight: 15, description: "Aggregated tone across reviews, comments and mentions.",      order: 1 },
  { name: "Review Quality",        enabled: true, weight: 12, description: "Depth, authenticity and detail of user reviews.",              order: 2 },
  { name: "Creator Trust",         enabled: true, weight: 10, description: "Reputation weight of creators covering the product.",          order: 3 },
  { name: "Brand Reputation",      enabled: true, weight: 12, description: "Historical brand trust and complaint resolution track record.", order: 4 },
  { name: "Product Performance",   enabled: true, weight: 12, description: "Real-world performance vs marketed spec.",                     order: 5 },
  { name: "Price Value",           enabled: true, weight: 8,  description: "Value delivered for the price paid.",                          order: 6 },
  { name: "Customer Satisfaction", enabled: true, weight: 8,  description: "Post-purchase satisfaction signals across support tickets.",   order: 7 },
  { name: "Trend Score",           enabled: true, weight: 5,  description: "Momentum of mentions and interest over time.",                 order: 8 },
  { name: "Return / Risk Signals", enabled: true, weight: 6,  description: "Return rate, warranty claims and reported defects.",           order: 9 },
  { name: "Social Intelligence",   enabled: true, weight: 7,  description: "Cross-platform social chatter and creator coverage.",          order: 10 },
  { name: "Product Verification",  enabled: true, weight: 5,  description: "GTIN / UPC / Barcode registered and verified.",               order: 11 },
];

export const DEFAULT_SIBASCORE_CONFIG: SibaScoreConfig = {
  identity: DEFAULT_SIBASCORE_IDENTITY,
  bounds: DEFAULT_SIBASCORE_BOUNDS,
  description: "Scored across 11 trust signals",
  ranges: DEFAULT_SIBASCORE_RANGES,
  signals: DEFAULT_SIBASCORE_SIGNALS,
};

export function tierForScore(
  score: number,
  ranges: SibaScoreRange[],
): SibaScoreRange {
  const s = Math.round(Math.max(0, score));
  const hit = ranges.find((r) => s >= r.min && s <= r.max);
  return hit || ranges[ranges.length - 1] || DEFAULT_SIBASCORE_RANGES[0];
}

export function useSibaScoreConfig(): SibaScoreConfig {
  const q = useQuery<SibaScoreConfig>({
    queryKey: ["sibascore-config"],
    queryFn: async () => (await api.get("/sibascore/config")).data,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
  return q.data || DEFAULT_SIBASCORE_CONFIG;
}

// -------- Backward-compat alias for iteration_95 consumers ------------

export interface SibaScoreRangesConfig {
  ranges: SibaScoreRange[];
  description: string;
  updated_at?: string | null;
}

export const DEFAULT_SIBASCORE_DESCRIPTION = DEFAULT_SIBASCORE_CONFIG.description;

export function useSibaScoreRanges(): SibaScoreRangesConfig {
  const cfg = useSibaScoreConfig();
  return {
    ranges: cfg.ranges,
    description: cfg.description,
    updated_at: cfg.updated_at ?? null,
  };
}
